// frontend/src/components/public-dosing/ElementSelector.tsx
import { useState } from 'react';
import { Select } from '@mantine/core';

// Standard trace elements for reef tanks
const TRACE_ELEMENTS = [
  'Aluminum',
  'Barium',
  'Boron',
  'Chromium',
  'Cobalt',
  'Copper',
  'Iodine',
  'Iron',
  'Lead',
  'Lithium',
  'Manganese',
  'Molybdenum',
  'Nickel',
  'Selenium',
  'Silver',
  'Strontium',
  'Tin',
  'Titanium',
  'Tungsten',
  'Vanadium',
  'Zinc',
];

interface ElementSelectorProps {
  onSelect: (element: string) => void;
  existingElements: string[];
  placeholder?: string;
}

export function ElementSelector({
  onSelect,
  existingElements,
  placeholder = '+ Add Element',
}: ElementSelectorProps) {
  const [searchValue, setSearchValue] = useState('');

  // Filter out already selected elements
  const availableElements = TRACE_ELEMENTS.filter(
    (el) => !existingElements.includes(el)
  );

  const handleChange = (selectedValue: string | null) => {
    if (!selectedValue) return;

    if (selectedValue === '__custom__') {
      // Use the search value as custom element name
      const customName = searchValue.trim();
      if (customName && !existingElements.includes(customName)) {
        onSelect(customName);
      }
    } else if (!existingElements.includes(selectedValue)) {
      onSelect(selectedValue);
    }
    setSearchValue('');
  };

  // Build options list with custom element option when search doesn't match exactly
  const searchLower = searchValue.toLowerCase();
  const hasExactMatch = availableElements.some(
    (el) => el.toLowerCase() === searchLower
  );
  const showCustomOption = searchValue.trim() && !hasExactMatch && !existingElements.includes(searchValue.trim());

  const options = [
    ...availableElements.map((e) => ({ value: e, label: e })),
    ...(showCustomOption
      ? [{ value: '__custom__', label: `+ Add "${searchValue.trim()}"` }]
      : []),
  ];

  return (
    <Select
      value={null}
      onChange={handleChange}
      data={options}
      placeholder={placeholder}
      searchable
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      clearable={false}
      size="sm"
      aria-label="Add element"
      style={{ width: 220 }}
    />
  );
}
